import {
  addMonthsToMonth,
  formatMoney,
  MAX_ZERO_REPORT_MONTHS,
  monthOf,
  monthRange,
  RETENTION_YEARS,
  roundCents,
  summarizeByMonth,
  yearOf,
} from "@pcj/shared";
import { FileText, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { type MonthFolder, MonthFolders } from "../../components/MonthFolders";
import { Banner, Button, DownloadLink, EmptyState, Pill, SectionTitle } from "../../components/ui";
import { plural } from "../../lib/format";
import { operationsReportUrl } from "../../lib/queries";
import { useCompany } from "./CompanyLayout";
import { OperationForm } from "./OperationForm";

/** Operations in one folder per month; each folder opens that month's operations. */
export function OperationsTab() {
  const { operations, notices, clients, thresholds, today, startMonth, companyId, can, readOnly } = useCompany();
  const navigate = useNavigate();
  const [formOpen, setFormOpen] = useState(false);
  const currentMonth = monthOf(today);

  const byMonth = useMemo(
    () => summarizeByMonth({ operations, notices, thresholds, today }),
    [operations, notices, thresholds, today],
  );
  const folders = useMemo((): MonthFolder[] => {
    // Every month since the records start (at most the retention period of empty
    // months), plus any older month that has operations.
    const earliest = addMonthsToMonth(currentMonth, -(MAX_ZERO_REPORT_MONTHS - 1));
    const months = new Set([
      ...monthRange(startMonth < earliest ? earliest : startMonth, currentMonth),
      ...byMonth.keys(),
    ]);
    return [...months].map((month) => {
      const summary = byMonth.get(month);
      if (!summary || summary.operations === 0) return { month, title: "Sin operaciones", empty: true };
      const { notices: n } = summary;
      return {
        month,
        title: plural(summary.operations, "operación", "operaciones"),
        detail: `${formatMoney(summary.amount)} sin IVA`,
        badges: (
          <>
            {n.pending > 0 && (
              <Pill tone={n.overdue > 0 ? "red" : "amber"}>
                {plural(n.pending, "Aviso pendiente", "Avisos pendientes")}
              </Pill>
            )}
            {n.total > 0 && n.pending === 0 && <Pill tone="green">Avisos presentados</Pill>}
            {summary.cashOverLimit > 0 && <Pill tone="red">Efectivo sobre el tope</Pill>}
          </>
        ),
      };
    });
  }, [byMonth, startMonth, currentMonth]);

  function yearSummary(year: number) {
    let count = 0;
    let amount = 0;
    for (const [month, summary] of byMonth) {
      if (yearOf(month) !== year) continue;
      count += summary.operations;
      amount = roundCents(amount + summary.amount);
    }
    return `${plural(count, "operación", "operaciones")} · ${formatMoney(amount)} sin IVA`;
  }

  return (
    <div className="space-y-4">
      <SectionTitle
        title="Registro de operaciones"
        subtitle="Una carpeta por mes · montos sin IVA · función 2 del art. 41 RCG"
        actions={
          <>
            {can("capture") && !readOnly && (
              <Button icon={Plus} onClick={() => setFormOpen((v) => !v)} disabled={clients.length === 0}>
                Nueva operación
              </Button>
            )}
            {operations.length > 0 && (
              <DownloadLink href={operationsReportUrl(companyId)}>Resumen general (PDF)</DownloadLink>
            )}
          </>
        }
      />
      {clients.length === 0 && (
        <Banner tone="info">Registra primero al menos un cliente en la pestaña Clientes.</Banner>
      )}
      {formOpen && (
        <OperationForm
          onDone={(created) => {
            setFormOpen(false);
            // Show the new operation inside its month.
            if (created) navigate(monthOf(created.date));
          }}
        />
      )}

      {operations.length === 0 ? (
        <EmptyState icon={FileText} title="Aún no hay operaciones registradas" />
      ) : (
        <>
          <MonthFolders folders={folders} currentMonth={currentMonth} yearSummary={yearSummary} />
          <p className="text-xs text-gray-500">
            Las operaciones se conservan al menos {RETENTION_YEARS} años (art. 18 LFPIORPI); el portal no las borra.
          </p>
        </>
      )}
    </div>
  );
}
